import React, { useState } from 'react';
import BaseWidget from './BaseWidget';
import WidgetFactory from './WidgetFactory';

/**
 * GroupWidget Component
 *
 * iPhone-style widget grouping that creates a parent tile containing sub-tiles.
 * When two widgets are dragged together, they form a group with expandable/collapsible view.
 */
const GroupWidget = ({
  id,
  config = {},
  onRemove,
  onConfigure,
  isEditing,
  theme = 'light',
  onUngroup,
  onRemoveFromGroup,
  metrics,
  isUser,
  areaAccess
}) => {
  const {
    title = 'Widget Group',
    groupedWidgets = [],
    expanded = false
  } = config;

  const [isExpanded, setIsExpanded] = useState(expanded);

  // Handle expanding/collapsing the group
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Handle removing a widget from the group
  const handleRemoveWidget = (widgetId) => {
    if (onRemoveFromGroup) {
      onRemoveFromGroup(id, widgetId);
    }
  };

  // Handle ungrouping (breaking the group apart)
  const handleUngroup = () => {
    if (onUngroup) {
      onUngroup(id);
    }
  };

  // Collapsed view - shows widget previews in a grid
  const CollapsedView = () => (
    <div className="h-full flex flex-col">
      {/* Header with widget count */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <div className="px-2 py-1 rounded text-xs font-medium bg-[var(--nav-active-bg)] text-[var(--nav-active-ink)]">
            {groupedWidgets.length} widgets
          </div>
        </div>
        <button
          onClick={toggleExpanded}
          className="p-1 rounded hover:bg-[var(--ink)]/10 transition-colors"
          title="Expand group"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Widget preview grid (2x2 or 2x3 depending on count) */}
      <div className="flex-1 grid grid-cols-2 gap-2 overflow-hidden">
        {groupedWidgets.slice(0, 6).map((widget, index) => (
          <div
            key={widget.id || index}
            className="rounded-lg border-2 border-dashed flex flex-col items-center justify-center p-2 transition-all cursor-pointer border-[var(--surface-border)] bg-[var(--surface-sunken)] hover:brightness-95"
            onClick={toggleExpanded}
            title={`${widget.type || 'Widget'} - Click to expand`}
          >
            {/* Widget icon/type indicator */}
            <div className="w-8 h-8 rounded-full flex items-center justify-center mb-1 bg-[var(--nav-active-bg)] text-[var(--nav-active-ink)]">
              {getWidgetIcon(widget.type)}
            </div>
            {/* Widget type label */}
            <span className="text-xs font-medium truncate w-full text-center text-[var(--ink)]">
              {formatWidgetType(widget.type)}
            </span>
          </div>
        ))}

        {/* Show "+X more" if there are more than 6 widgets */}
        {groupedWidgets.length > 6 && (
          <div
            className="rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer border-[var(--surface-border)] bg-[var(--surface-sunken)] hover:brightness-95"
            onClick={toggleExpanded}
          >
            <span className="text-sm font-medium text-[var(--ink-muted)]">
              +{groupedWidgets.length - 6}
            </span>
          </div>
        )}
      </div>

      {/* Ungroup button (only in edit mode) */}
      {isEditing && (
        <button
          onClick={handleUngroup}
          className="mt-3 w-full py-2 text-xs font-medium rounded border transition-colors border-[var(--surface-border)] text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)]"
        >
          <svg className="w-3 h-3 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Ungroup Widgets
        </button>
      )}
    </div>
  );

  // Expanded view - shows full widgets in a vertical list
  const ExpandedView = () => (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header with collapse button */}
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-semibold text-[var(--ink)]">
            {title}
          </h3>
          <div className="px-2 py-0.5 rounded text-xs bg-[var(--surface-sunken)] text-[var(--ink-muted)]">
            {groupedWidgets.length}
          </div>
        </div>
        <button
          onClick={toggleExpanded}
          className="p-1 rounded hover:bg-[var(--ink)]/10 transition-colors"
          title="Collapse group"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      </div>

      {/* Scrollable widget list */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {groupedWidgets.map((widget, index) => (
          <div
            key={widget.id || index}
            className="relative rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)]"
          >
            {/* Render actual widget using WidgetFactory - scaled down 25% */}
            <div className="relative" style={{ transform: 'scale(0.75)', transformOrigin: 'top left', width: '133.33%', height: '133.33%' }}>
              <WidgetFactory
                widget={widget}
                onRemove={isEditing ? () => handleRemoveWidget(widget.id) : null}
                onConfigure={null}
                isEditing={false}
                theme={theme}
                metrics={metrics}
                isUser={isUser}
                areaAccess={areaAccess}
              />

              {/* Remove from group button overlay (only in edit mode) */}
              {isEditing && (
                <button
                  onClick={() => handleRemoveWidget(widget.id)}
                  className="absolute top-2 right-2 p-1.5 rounded-full transition-all z-10 bg-[var(--surface-raised)]/90 text-[var(--ui-alert)] hover:bg-[var(--ui-alert)]/10 shadow-lg"
                  title="Remove from group"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Ungroup button (only in edit mode) */}
      {isEditing && (
        <button
          onClick={handleUngroup}
          className="mt-3 w-full py-2 text-xs font-medium rounded border transition-colors flex-shrink-0 border-[var(--surface-border)] text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)]"
        >
          <svg className="w-3 h-3 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          Ungroup All Widgets
        </button>
      )}
    </div>
  );

  return (
    <BaseWidget
      id={id}
      title={title}
      onRemove={onRemove}
      onConfigure={onConfigure}
      isRemovable={isEditing}
      isConfigurable={false}
      theme={theme}
      icon={
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      }
      headerActions={
        isEditing ? (
          <div className="widget-drag-handle px-2 py-1 cursor-move">
            <svg className="w-4 h-4 text-[var(--ink-muted)]" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </div>
        ) : null
      }
    >
      {isExpanded ? <ExpandedView /> : <CollapsedView />}
    </BaseWidget>
  );
};

// Helper function to get widget icon
const getWidgetIcon = (widgetType) => {
  const iconMap = {
    'kpi': '📊',
    'chart': '📈',
    'table': '📋',
    'recent-activity': '🔔',
    'quick-stats': '⚡',
    'goals': '🎯',
    'calendar': '📅',
    'tasks': '✓',
    'default': '📦'
  };

  return iconMap[widgetType] || iconMap['default'];
};

// Helper function to format widget type for display
const formatWidgetType = (widgetType) => {
  if (!widgetType) return 'Widget';

  // Convert kebab-case or camelCase to Title Case
  return widgetType
    .replace(/[-_]/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

// Helper function to get widget preview content
const getWidgetPreview = (widget) => {
  const config = widget.config || {};

  // Return a simple preview based on widget type
  switch (widget.type) {
    case 'kpi':
      return config.title || 'KPI Widget';
    case 'chart':
      return config.chartType ? `${config.chartType} Chart` : 'Chart Widget';
    case 'table':
      return config.title || 'Table Widget';
    case 'recent-activity':
      return 'Recent Activity Feed';
    case 'quick-stats':
      return 'Quick Statistics';
    case 'goals':
      return 'Goals Tracker';
    default:
      return widget.type || 'Widget';
  }
};

export default GroupWidget;
